<template>
  <div v-if="isAvailable" ref="root" class="record-fields-manager" @click.stop>
    <button
      type="button"
      class="btn btn-sm btn-light-primary record-fields-toggle"
      :class="{ active: isOpen }"
      title="Remover ou incluir campos do modelo neste registro"
      @click="toggleMenu"
    >
      <i class="bi bi-sliders"></i>
      <span>Campos</span>
      <span v-if="removedCount" class="badge badge-circle badge-warning record-fields-count">{{ removedCount }}</span>
    </button>

    <div v-if="isOpen" class="record-fields-menu">
      <div class="record-fields-menu-header">
        <span class="record-fields-menu-title">Campos deste registro</span>
        <span class="record-fields-menu-hint">
          Desligue para remover o campo. O conteúdo fica guardado e volta quando o campo for incluído de novo.
        </span>
      </div>

      <div class="record-fields-menu-list">
        <label
          v-for="attribute in attributes"
          :key="attribute.id_medrec_attr"
          class="record-fields-menu-item"
          :class="{ 'is-removed': !isActive(attribute) }"
        >
          <span class="form-check form-switch form-check-custom form-check-solid">
            <input
              class="form-check-input h-20px w-30px"
              type="checkbox"
              :checked="isActive(attribute)"
              :disabled="savingAttributeId === attribute.id_medrec_attr"
              @change="toggleAttribute(attribute, $event)"
            >
          </span>
          <span class="record-fields-menu-label">{{ attribute.label }}</span>
          <span
            v-if="!isActive(attribute) && hasContent(attribute)"
            class="badge badge-light-warning"
            title="O campo foi removido mas o conteúdo continua guardado"
          >com conteúdo</span>
          <span v-if="savingAttributeId === attribute.id_medrec_attr" class="spinner-border spinner-border-sm text-primary"></span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../../helpers/http";
import Alert from "../../../helpers/alert";
import permission from "../../../helpers/permission";
import { isAttributeActive, manageableAttributes, removedAttributes } from './record-attributes';

export default {
  name: 'RecordAttributesManager',
  props: {
    medicalRecord: {
      type: Object,
      required: true,
    },
    schedule: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['changed'],
  data() {
    return {
      isOpen: false,
      savingAttributeId: null,
    };
  },
  computed: {
    attributes() {
      return manageableAttributes(this.medicalRecord?.attributes);
    },
    removedCount() {
      return removedAttributes(this.attributes).length;
    },
    isAvailable() {
      return !this.disabled
        && this.attributes.length > 0
        && permission.has('Update Medical Record Attribute');
    },
  },
  watch: {
    isAvailable(available) {
      if (!available) {
        this.closeMenu();
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.closeOnOutsideClick);
  },
  methods: {
    isActive(attribute) {
      return isAttributeActive(attribute);
    },
    hasContent(attribute) {
      const text = attribute?.text;
      if (Array.isArray(text)) {
        return text.length > 0;
      }
      if (text === null || text === undefined) {
        return false;
      }

      return String(text).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '';
    },
    toggleMenu() {
      if (this.isOpen) {
        this.closeMenu();
        return;
      }

      this.isOpen = true;
      document.addEventListener('mousedown', this.closeOnOutsideClick);
    },
    closeMenu() {
      this.isOpen = false;
      document.removeEventListener('mousedown', this.closeOnOutsideClick);
    },
    closeOnOutsideClick(event) {
      if (!this.$refs.root?.contains(event.target)) {
        this.closeMenu();
      }
    },
    toggleAttribute(attribute, event) {
      const active = event.target.checked;
      const url = `/professionals/${this.schedule?.id_prof}/patients/${this.schedule?.id_patient}`
        + `/medical-records/${attribute.id_medrec}/attributes/${attribute.id_medrec_attr}`;

      this.savingAttributeId = attribute.id_medrec_attr;

      http.put(url, { active }).then(() => {
        attribute.active = active;
        Alert.toastSuccess(active
          ? `Campo "${attribute.label}" incluído novamente.`
          : `Campo "${attribute.label}" removido do registro.`);
        this.$emit('changed', attribute);
      }).catch((e) => {
        event.target.checked = !active;
        Alert.toastError(
          e.response?.data?.errors?.active?.[0]
          ?? e.response?.data?.message
          ?? 'Não foi possível atualizar o campo.'
        );
      }).finally(() => {
        this.savingAttributeId = null;
      });
    },
  },
};
</script>

<style scoped>
.record-fields-manager {
  position: relative;
  cursor: default;
}

.record-fields-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.record-fields-count {
  width: 18px;
  height: 18px;
  font-size: 0.7rem;
}

.record-fields-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 1060;
  width: 320px;
  max-width: 90vw;
  background: var(--bs-body-bg, #fff);
  border: 1px solid var(--bs-border-color, #e4e6ef);
  border-radius: 0.65rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
}

.record-fields-menu-header {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--bs-border-color, #e4e6ef);
}

.record-fields-menu-title {
  font-weight: 600;
  font-size: 0.95rem;
}

.record-fields-menu-hint {
  font-size: 0.8rem;
  color: var(--bs-gray-600, #7e8299);
}

.record-fields-menu-list {
  max-height: 320px;
  overflow-y: auto;
  padding: 0.35rem 0;
}

.record-fields-menu-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 1rem;
  margin: 0;
  cursor: pointer;
}

.record-fields-menu-item:hover {
  background: var(--bs-gray-100, #f5f8fa);
}

.record-fields-menu-item.is-removed .record-fields-menu-label {
  color: var(--bs-gray-500, #a1a5b7);
  text-decoration: line-through;
}

.record-fields-menu-label {
  flex: 1;
  font-size: 0.9rem;
}
</style>
